import { Award, ShieldCheck, Factory, Wrench, Target, Eye, Cpu, CheckCircle2, MapPin, Users } from "lucide-react";
import Reveal from "../components/Reveal";

const TIMELINE = [
  { year: "2016", text: "Universal Electricals & Electronics founded in KG Halli, Bangalore — set up as an Electronics R&D and Mining Safety Equipment Trading + Service company." },
  { year: "2018", text: "Expanded mining safety portfolio — AFDSS, Audio Visual Alarms, Seat Belt Alarm, Body Lifting Alarm." },
  { year: "2020", text: "Added Anti-Tilt Warning Devices, Rear-View Camera Systems, and Driver Fatigue Warning solutions for HEMM fleets." },
  { year: "2022", text: "Introduced control products line — VFDs, Work Lamps, LIDAR & Ultrasonic Proximity Systems." },
  { year: "2024", text: "Strengthened pan-India service network for installation, calibration, and post-sale field engineering." },
  { year: "2026", text: "Continuing to deliver value-based safety solutions to mining and heavy-vehicle operators across India." },
];

const CERTS = [
  { code: "DGMS", text: "Compliance for HEMM mining vehicle safety devices." },
  { code: "ISO 9001:2015", text: "Quality Management System aligned." },
  { code: "AS 5062 / IS 15683", text: "Vehicle fire suppression compliance." },
  { code: "BS 7905", text: "Reverse alarm broadband tone compliance." },
];

const HIGHLIGHTS = [
  "Founded 2016 · KG Halli, Bangalore",
  "Electronics R&D + Mining Safety Service",
  "AFDSS · Anti-Tilt · Rear-View Camera · DMS",
  "VFD · Work Lamps · LIDAR / Ultrasonic Proximity",
  "Skilled, technically deep engineering team",
  "Competitive pricing tuned to customer needs",
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative h-[55vh] min-h-[400px] overflow-hidden bg-slate-900">
        <img
          src="https://images.pexels.com/photos/31543700/pexels-photo-31543700.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt=""
          className="w-full h-full object-cover ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 to-slate-950/30" />
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="absolute inset-0 max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 flex flex-col justify-end pb-14 md:pb-16">
          <Reveal>
            <div className="overline text-amber-400 mb-3">About Us</div>
            <h1 className="h-display text-white max-w-4xl">
              Welcome to Universal Electricals &amp; Electronics.
            </h1>
          </Reveal>
        </div>
      </section>

      {/* COMPANY PROFILE */}
      <section className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 py-20 md:py-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <Reveal y={32} className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/10006261/pexels-photo-10006261.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Field engineers and mining safety equipment"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-amber-500 p-5 md:p-6 max-w-[60%] hidden md:block shadow-xl">
                <div className="font-mono text-[10px] tracking-widest text-slate-900 font-bold">SINCE 2016</div>
                <div className="font-heading font-extrabold text-xl md:text-2xl text-slate-950 mt-1 leading-tight">
                  Bangalore-based E&amp;E specialist
                </div>
              </div>
              <div className="absolute -top-4 -left-4 w-24 h-24 border-l-4 border-t-4 border-amber-500" />
            </div>
          </Reveal>

          <div className="lg:col-span-7 space-y-6">
            <Reveal>
              <div className="overline mb-3">Company Profile</div>
              <h2 className="h-section">An Electronics R&amp;D &amp; Mining Safety partner.</h2>
            </Reveal>
            <Reveal delay={100} className="space-y-4 text-slate-700 leading-relaxed">
              <p>
                Founded in <strong>2016 in KG Halli, Bangalore</strong>, Universal Electricals
                &amp; Electronics set its foot as an <strong>Electronics R&amp;D and Mining
                Safety Equipment Trading and Service Company</strong> &mdash; growing steadily
                over the years into a trusted partner for heavy-vehicle and industrial customers.
              </p>
              <p>
                As an <strong>E&amp;E solutions specialist</strong>, we offer a focused range
                of Mining Safety Equipment products covering <strong>AFDSS, Audio Visual
                Alarm, Seat Belt Alarm, Body Lifting Alarm, Warning Harness, Anti-Tilt
                Warning Device, Rear View Camera System, Driver Fatigue Warning Device</strong>,
                and other related safety equipment.
              </p>
              <p>
                We also offer control products such as <strong>Variable Frequency Drives,
                Work Lamps, Proximity Systems based on LIDAR, Ultrasonic Devices</strong>,
                and similar solutions tailored to industrial and mining workflows.
              </p>
              <p>
                Our team is composed of <strong>technically skilled and highly dedicated
                personnel</strong> working cohesively to promote value-based solutions with
                a competitive pricing structure to suit customer needs.
              </p>
            </Reveal>

            <Reveal delay={200}>
              <div className="grid sm:grid-cols-2 gap-3 mt-8 pt-8 border-t border-slate-200">
                {HIGHLIGHTS.map((h, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" strokeWidth={2.2} />
                    <span className="text-sm text-slate-700 font-medium">{h}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MISSION VISION */}
      <section className="bg-slate-950 text-white py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="relative max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-px bg-slate-800">
          <Reveal>
            <div className="bg-slate-950 p-8 md:p-12 h-full">
              <Target className="w-12 h-12 text-amber-400 mb-6" strokeWidth={2} />
              <div className="overline mb-2 text-amber-400">Mission</div>
              <h3 className="h-section text-white mb-4">Value-based safety solutions.</h3>
              <p className="text-slate-300 leading-relaxed">
                Equip mining and industrial customers with safety, monitoring, and control
                products that deliver real value &mdash; backed by skilled engineering, fair
                pricing, and dependable post-sale service.
              </p>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="bg-slate-950 p-8 md:p-12 h-full">
              <Eye className="w-12 h-12 text-amber-400 mb-6" strokeWidth={2} />
              <div className="overline mb-2 text-amber-400">Vision</div>
              <h3 className="h-section text-white mb-4">Trusted E&amp;E specialist.</h3>
              <p className="text-slate-300 leading-relaxed">
                To be the most trusted Electronics R&amp;D and Mining Safety Equipment partner
                for India&rsquo;s heavy-vehicle, mining, and industrial sectors &mdash; recognised
                for technical depth and customer-first delivery.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 py-20 md:py-24">
        <Reveal>
          <div className="overline mb-3">Capabilities</div>
          <h2 className="h-section mb-10 md:mb-12">Engineering, supply, install, service.</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {[
            { icon: Cpu, title: "Electronics R&D", text: "Hands-on R&D for mining safety and industrial control electronics." },
            { icon: Factory, title: "Equipment Trading", text: "Curated range of mining safety + E&E control products from trusted OEMs." },
            { icon: Wrench, title: "Field Service", text: "Installation, calibration, troubleshooting, and post-sale support across India." },
            { icon: Users, title: "Technical Team", text: "Skilled, dedicated engineers focused on value-based customer outcomes." },
          ].map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={i} delay={i * 100}>
                <div className="card-premium p-6 h-full">
                  <Icon className="w-10 h-10 text-amber-500 mb-4" strokeWidth={2} />
                  <h4 className="h-card mb-2">{c.title}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* TIMELINE */}
      <section className="bg-slate-100 py-20 md:py-24">
        <div className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8">
          <Reveal>
            <div className="overline mb-3">Our Journey</div>
            <h2 className="h-section mb-10 md:mb-12">From a Bangalore workshop to a national partner.</h2>
          </Reveal>
          <div className="relative pl-8 md:pl-10 border-l-2 border-amber-500 space-y-8 max-w-3xl">
            {TIMELINE.map((t, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="relative">
                  <div className="absolute -left-[42px] md:-left-[50px] w-5 h-5 bg-amber-500 border-4 border-slate-100" />
                  <div className="font-mono text-xs text-amber-600 tracking-widest font-bold mb-1">{t.year}</div>
                  <p className="text-slate-800 leading-relaxed font-medium">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 py-20 md:py-24">
        <Reveal>
          <div className="overline mb-3">Compliance</div>
          <h2 className="h-section mb-10 md:mb-12">Aligned with mining-safety standards.</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200">
          {CERTS.map((c, i) => (
            <Reveal key={i} delay={i * 80}>
              <div className="bg-white p-6 hover:bg-slate-50 transition-colors h-full">
                <Award className="w-8 h-8 text-amber-500 mb-3" strokeWidth={2} />
                <div className="font-mono text-xs font-bold text-amber-600 tracking-widest mb-2">
                  {c.code}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* LOCATION CALLOUT */}
      <section className="bg-carbon-950 text-white py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <div className="relative max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="overline mb-2 text-amber-400">Headquartered</div>
            <h3 className="h-section">Abbigere, Bangalore.</h3>
            <div className="mt-3 flex items-start gap-2 text-slate-300">
              <MapPin className="w-4 h-4 mt-1 text-amber-400" />
              <span className="text-sm leading-relaxed">
                15 & 16, 1st Floor, Girija Arcade, Lakshmipura Main Road, Opp PMK, Abbigere, Bangalore - 560090
              </span>
            </div>
          </div>
          <ShieldCheck className="w-16 h-16 text-amber-500/20" />
        </div>
      </section>
    </div>
  );
}
